import React, { type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { type Appearance } from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';

/*
A custom button component for the footer of the JQL basic picker dropdown button
with a tooltip and custom alignment to the left
*/
export const LeftFooterButton = ({
	message,
	onClick,
	testId,
	appearance,
	hasTooltip,
	label,
}: {
	message: string;
	onClick: (mouseEvent: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	testId?: string;
	appearance: Appearance;
	hasTooltip: boolean;
	label?: string;
}) => {
	const shouldApplyVisualRefresh = isVisualRefreshEnabled();

	const content = (
		<Button appearance={appearance} onClick={onClick} testId={testId}>
			{message}
		</Button>
	);

	if (!hasTooltip) {
		if (shouldApplyVisualRefresh) {
			return (
				<Box padding="space.050" xcss={leftFooterButtonContainerStyles}>
					{content}
				</Box>
			);
		}
		return (
			<LeftFooterButtonContainerOld>
				<Button appearance={appearance} onClick={onClick} testId={testId}>
					{message}
				</Button>
			</LeftFooterButtonContainerOld>
		);
	}

	if (shouldApplyVisualRefresh) {
		return (
			<Box padding="space.050" xcss={leftFooterButtonContainerStyles}>
				<Tooltip content={message}>{content}</Tooltip>
			</Box>
		);
	}

	return (
		<LeftFooterButtonContainerOld>
			<Tooltip content={message}>
				<Button
					appearance={appearance}
					onClick={onClick}
					testId={testId}
					{...(label && { 'aria-label': label })}
				>
					{message}
				</Button>
			</Tooltip>
		</LeftFooterButtonContainerOld>
	);
};

const leftFooterButtonContainerStyles = xcss({
	overflow: 'hidden',
	marginInlineStart: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftFooterButtonContainerOld = styled.div({
	overflow: 'hidden',
	// Required to ensure focus outline does not get cut off
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.050'),
	// left padding + left margin should total to 12px to match other picker types.
	marginInlineStart: token('space.100'),
});
